import { useEffect, useCallback, useRef } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { useSocketService } from '@/context/SocketServiceContext';
import Kedo from '@/Kedo';
import { Block, BlockRef } from '../types';

interface UseBlockSocketOptions {
    kedo: Kedo;
    block: Block;
    blockRef: React.RefObject<BlockRef | null>;
    onNewDossier?: (dossierId: number) => void;
}

interface SocketData {
    sender: {
        id: string;
        userId: number;
        username: string;
    };
    displayItem: number;
    dossierId: number;
}

const TOAST_OPTIONS: ToastOptions = {
    position: 'bottom-center',
    autoClose: 2500,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
};

export const useBlockSocket = ({
    kedo,
    block,
    blockRef,
    onNewDossier,
}: UseBlockSocketOptions): { emitUpdate: (dossierId: number) => void } => {
    const socketService = useSocketService();
    const isSetup = useRef(false);

    const handleRefresh = useCallback(
        (data: SocketData) => {
            const socket = socketService.socket();
            if (!socket) return;

            if (socket.id !== data.sender.id && data.displayItem === block.id) {
                if (blockRef.current) {
                    blockRef.current.fetchNewDossier(data.dossierId);
                }

                onNewDossier?.(data.dossierId);

                if (kedo.user().getUserId() !== data.sender.userId) {
                    toast.success(
                        kedo.t('dossier_errors.success_create_ws', {
                            username: data.sender.username,
                        }),
                        {
                            ...TOAST_OPTIONS,
                            toastId: `dossier_errors.success_create_ws_${data.dossierId}`,
                        }
                    );
                }
            }
        },
        [socketService, block.id, blockRef, kedo, onNewDossier]
    );

    useEffect(() => {
        if (isSetup.current) return;

        socketService.displayItem.refreshListener(handleRefresh);
        isSetup.current = true;

        return () => {
            socketService.displayItem.removeDdiListeners();
        };
    }, [socketService, handleRefresh]);

    const emitUpdate = useCallback(
        (dossierId: number) => {
            const socket = socketService.socket();
            if (!socket) return;

            setTimeout(() => {
                socketService.displayItem.update({
                    dossierId,
                    sender: socket.id,
                    displayItem: block.id,
                });

                if (blockRef.current) {
                    blockRef.current.fetchNewDossier(dossierId);
                }
            }, 100);
        },
        [socketService, block.id, blockRef]
    );

    return { emitUpdate };
};
